import React, { useEffect, useState } from 'react';
import './App.css';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MailOutlineIcon from '@mui/icons-material/EmailOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import { useTheme } from '@mui/material/styles';
import { useColorMode } from './colorMode';
import {
  ABOUT,
  BOARD,
  CONTACT_EMAIL,
  COORDINATOR_ROLES,
  EVENTS,
  FACEBOOK_URL,
  HERO_BANNER,
  HISTORY_ARTICLE,
  HISTORY_PHOTOS,
  HISTORY_RESOURCES,
  SCHOOL_DRAWING,
  INVOLVEMENT,
  LEADERSHIP_OTHER,
  LOCATION,
  MEETINGS,
  MEETING_MINUTES,
  MEMBERSHIP_URL,
  PTA_NAME,
  RESOURCES,
  SCHOOL_HISTORY,
  SCHOOL_NAME,
  SEASONAL_EVENTS,
  SEASONAL_EVENTS_FOOTNOTE,
  STATS,
  VOLUNTEER_EMAIL,
  VOLUNTEER_INTRO,
  VOLUNTEER_NOTE,
  VOLUNTEER_STEPS,
  WCSD_VOLUNTEER_URL,
  WISH_LISTS,
  WISH_LISTS_HEADING,
} from './siteContent';

const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  `${PTA_NAME} — inquiry`
)}`;
const VOLUNTEER_MAILTO = `mailto:${VOLUNTEER_EMAIL}?subject=${encodeURIComponent(
  `${PTA_NAME} — get involved`
)}`;
const volunteerRoleMailto = (role) =>
  `mailto:${VOLUNTEER_EMAIL}?subject=${encodeURIComponent(
    `${PTA_NAME} — Volunteer: ${role}`
  )}&body=${encodeURIComponent(
    `Hi Hanby PTA,\n\nI'm interested in helping with the "${role}" volunteer role. ` +
      `Please let me know how I can get involved.\n\nThanks!`
  )}`;

const SECTION_SCROLL_MS = 1800;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function scrollToSectionHash(hash, durationMs = SECTION_SCROLL_MS) {
  const id = hash.startsWith('#') ? hash.slice(1) : hash;
  const el = document.getElementById(id);
  if (!el) return;

  const nav = document.querySelector('.App-nav');
  const offset = (nav?.offsetHeight ?? 0) + 12;
  const startY = window.scrollY;
  const targetY = Math.max(0, el.getBoundingClientRect().top + startY - offset);
  const distance = targetY - startY;
  const idHash = `#${id}`;
  const syncHash = () => {
    if (window.location.hash !== idHash) {
      window.history.pushState(null, '', idHash);
    }
  };

  if (Math.abs(distance) < 2) {
    syncHash();
    return;
  }

  if (prefersReducedMotion()) {
    window.scrollTo({ top: targetY });
    syncHash();
    return;
  }

  const start = performance.now();
  const step = (now) => {
    const t = Math.min(1, (now - start) / durationMs);
    window.scrollTo(0, startY + distance * easeInOutCubic(t));
    if (t < 1) {
      requestAnimationFrame(step);
    } else {
      syncHash();
    }
  };
  requestAnimationFrame(step);
}

function SectionHeading({ kicker, title, subtitle, id }) {
  return (
    <Stack id={id} spacing={1} sx={{ mb: 3 }}>
      {kicker ? (
        <Typography variant="overline" color="primary.main">
          {kicker}
        </Typography>
      ) : null}
      <Typography
        variant="h2"
        component="h2"
        sx={{ fontSize: { xs: '1.6rem', sm: '2rem' } }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 720, lineHeight: 1.7, whiteSpace: 'pre-line' }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
  );
}

const NAV_LINKS = [
  { href: '#about', label: 'About' },
  { href: '#events', label: 'Events' },
  { href: '#volunteer', label: 'Volunteer' },
  { href: '#board', label: 'Board' },
  { href: '#wishlists', label: 'Wish Lists' },
  { href: '#history', label: 'History' },
  { href: '#resources', label: 'Resources' },
];

function useActiveSection(ids) {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const compute = () => {
      const nav = document.querySelector('.App-nav');
      const offset = (nav?.offsetHeight ?? 0) + 28;

      // At the very bottom, the last section is active even if its top is above the line.
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 4;
      if (atBottom) {
        setActiveId(ids[ids.length - 1]);
        return;
      }

      let current = '';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) current = id;
      }
      setActiveId(current);
    };

    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [ids]);

  return activeId;
}

const NAV_IDS = NAV_LINKS.map((l) => l.href.slice(1));

// Season accents tuned per mode: the light values fail WCAG contrast as bold
// labels on dark backgrounds, so dark mode uses lighter shades.
const SEASON_ACCENTS_LIGHT = {
  Fall: '#d97706',
  Winter: '#2563eb',
  Spring: '#16a34a',
  Summer: '#db2777',
  'Year-round': '#5b21b6',
};
const SEASON_ACCENTS_DARK = {
  Fall: '#fbbf24',
  Winter: '#60a5fa',
  Spring: '#4ade80',
  Summer: '#f472b6',
  'Year-round': '#a78bfa',
};

const THEME_ORDER = ['system', 'light', 'dark'];
const THEME_LABEL = { system: 'System', light: 'Light', dark: 'Dark' };
const THEME_ICON = {
  system: <BrightnessAutoIcon fontSize="small" />,
  light: <LightModeIcon fontSize="small" />,
  dark: <DarkModeIcon fontSize="small" />,
};

function PhotoLightbox({ photos, index, onClose, onNavigate }) {
  const open = index != null;
  const photo = open ? photos[index] : null;

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onNavigate(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onNavigate(1);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={handleKeyDown}
      maxWidth="lg"
      aria-label="Photo viewer"
      slotProps={{ paper: { sx: { bgcolor: '#000', m: { xs: 1, sm: 2 } } } }}
    >
      {photo ? (
        <Box sx={{ position: 'relative', lineHeight: 0 }}>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.45)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {photos.length > 1 ? (
            <>
              <IconButton
                onClick={() => onNavigate(-1)}
                aria-label="Previous photo"
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.45)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => onNavigate(1)}
                aria-label="Next photo"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.45)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          ) : null}
          <Box
            component="img"
            src={photo.src}
            alt={photo.caption}
            sx={{
              display: 'block',
              maxWidth: '92vw',
              maxHeight: '78vh',
              width: 'auto',
              height: 'auto',
              mx: 'auto',
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: '#fff', px: 2, py: 1.5, textAlign: 'center', lineHeight: 1.4 }}
          >
            {photo.caption}
          </Typography>
        </Box>
      ) : null}
    </Dialog>
  );
}

function App() {
  const { isDark, preference, cycleMode } = useColorMode();
  const theme = useTheme();
  const seasonAccents = isDark ? SEASON_ACCENTS_DARK : SEASON_ACCENTS_LIGHT;

  const nextPreference =
    THEME_ORDER[(THEME_ORDER.indexOf(preference) + 1) % THEME_ORDER.length];
  const themeToggle = {
    currentLabel: THEME_LABEL[preference],
    nextLabel: THEME_LABEL[nextPreference],
    icon: THEME_ICON[preference],
  };

  const activeId = useActiveSection(NAV_IDS);

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const navigatePhotos = (delta) =>
    setLightboxIndex(
      (i) => (i + delta + HISTORY_PHOTOS.length) % HISTORY_PHOTOS.length
    );

  return (
    <Box
      className="App-root"
      sx={{
        bgcolor: 'background.default',
        backgroundImage: isDark
          ? 'radial-gradient(ellipse 100% 70% at 50% -15%, rgba(91, 33, 182, 0.15), transparent 55%), radial-gradient(ellipse 55% 45% at 100% 30%, rgba(22, 163, 74, 0.10), transparent 50%), radial-gradient(ellipse 50% 40% at 0% 85%, rgba(91, 33, 182, 0.08), transparent 50%)'
          : 'radial-gradient(ellipse 100% 70% at 50% -15%, rgba(91, 33, 182, 0.09), transparent 55%), radial-gradient(ellipse 55% 45% at 100% 30%, rgba(22, 163, 74, 0.07), transparent 50%), radial-gradient(ellipse 50% 40% at 0% 85%, rgba(91, 33, 182, 0.05), transparent 50%)',
        backgroundAttachment: 'fixed',
      }}
    >
      <Box component="a" href="#main-content" className="App-skipLink">
        Skip to main content
      </Box>
      <AppBar
        position="sticky"
        elevation={0}
        className="App-nav"
        sx={{
          bgcolor: isDark ? 'rgba(15, 23, 42, 0.88)' : 'rgba(255, 255, 255, 0.85)',
          color: 'text.primary',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Typography
            component="a"
            href="#top"
            variant="subtitle1"
            onClick={(e) => {
              e.preventDefault();
              if (prefersReducedMotion()) {
                window.scrollTo({ top: 0 });
              } else {
                const startY = window.scrollY;
                const start = performance.now();
                const step = (now) => {
                  const t = Math.min(1, (now - start) / SECTION_SCROLL_MS);
                  window.scrollTo(0, startY * (1 - easeInOutCubic(t)));
                  if (t < 1) requestAnimationFrame(step);
                };
                requestAnimationFrame(step);
              }
              if (window.location.hash) {
                window.history.pushState(null, '', window.location.pathname);
              }
            }}
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.015em',
              color: 'primary.main',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            {PTA_NAME}
          </Typography>
          <Box
            component="nav"
            aria-label="Primary"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: { xs: 0.25, sm: 0.5 },
            }}
          >
            {NAV_LINKS.map((link) => {
              const isActive = activeId === link.href.slice(1);
              return (
                <Button
                  key={link.href}
                  color="inherit"
                  href={link.href}
                  size="small"
                  aria-current={isActive ? 'location' : undefined}
                  disableRipple
                  sx={{
                    textTransform: 'none',
                    display: { xs: 'none', md: 'inline-flex' },
                    borderRadius: 0,
                    px: 1,
                    // Constant weight so the active item never changes width and
                    // shifts its neighbors — active is shown via color + underline.
                    fontWeight: 600,
                    color: isActive ? 'primary.main' : 'inherit',
                    borderBottom: '2px solid',
                    borderColor: isActive ? 'primary.main' : 'transparent',
                    transition: 'color 0.2s, border-color 0.2s',
                    '&:hover': {
                      bgcolor: 'transparent',
                      borderColor: isActive
                        ? 'primary.main'
                        : 'rgba(91, 33, 182, 0.35)',
                    },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSectionHash(link.href);
                  }}
                >
                  {link.label}
                </Button>
              );
            })}
            <Tooltip title={`Theme: ${themeToggle.currentLabel} (switch to ${themeToggle.nextLabel})`}>
              <IconButton
                onClick={cycleMode}
                color="inherit"
                size="small"
                aria-label={`Theme: ${themeToggle.currentLabel}. Switch to ${themeToggle.nextLabel}.`}
                sx={{ ml: 0.5 }}
              >
                {themeToggle.icon}
              </IconButton>
            </Tooltip>
            <Button
              href={MAILTO}
              variant="contained"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                ml: { md: 1 },
              }}
            >
              Contact
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="main" id="main-content" tabIndex={-1} sx={{ outline: 'none' }}>
      <Box className="App-hero" sx={{ position: 'relative' }}>
        <Box
          className="App-heroMedia"
          sx={{
            backgroundImage:
              'linear-gradient(105deg, rgba(91,33,182,0.06) 0%, rgba(255,255,255,0) 45%, rgba(22,163,74,0.06) 100%)',
          }}
        />
        <Container
          maxWidth="lg"
          sx={{ position: 'relative', zIndex: 1, py: { xs: 6, sm: 10 } }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 4, md: 6 },
            }}
          >
            <Stack spacing={2.5} sx={{ flex: 1, maxWidth: 620 }}>
              <Typography
                variant="overline"
                color="primary.main"
                sx={{ fontWeight: 700 }}
              >
                {SCHOOL_NAME} · {LOCATION}
              </Typography>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: '2.25rem', sm: '3.25rem' },
                  color: 'text.primary',
                }}
              >
                Welcome to the Hanby PTA.
              </Typography>
              <Typography
                variant="h5"
                component="p"
                color="text.secondary"
                sx={{
                  fontWeight: 400,
                  fontSize: { xs: '1.05rem', sm: '1.2rem' },
                  lineHeight: 1.55,
                  maxWidth: 580,
                }}
              >
                Parents, guardians, and educators working together to make this
                school year truly exceptional for every Hanby student.
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ pt: 1 }}
              >
                <Button
                  href={VOLUNTEER_MAILTO}
                  variant="contained"
                  size="large"
                  sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}
                >
                  Get involved
                </Button>
                <Button
                  href="#events"
                  variant="outlined"
                  size="large"
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSectionHash('#events');
                  }}
                >
                  See upcoming events
                </Button>
              </Stack>
            </Stack>
            <Box
              component="img"
              src={HERO_BANNER}
              alt="Hanby — colorful mosaic artwork spelling the school name"
              sx={{
                width: { xs: '100%', md: '44%' },
                maxWidth: 460,
                borderRadius: 4,
                boxShadow: isDark
                  ? '0 12px 32px rgba(0, 0, 0, 0.5)'
                  : '0 12px 32px rgba(15, 23, 42, 0.16)',
                border: `4px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#fff'}`,
              }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 5 } }}>
        <Card sx={{ mb: 6, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
              justifyContent: 'space-between',
            }}
          >
            {STATS.map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 ? (
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      width: '1px',
                      alignSelf: 'stretch',
                      bgcolor: 'divider',
                      mx: { sm: 2 },
                    }}
                  />
                ) : null}
                <Stack
                  spacing={0.5}
                  sx={{
                    textAlign: { xs: 'left', sm: 'center' },
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="h3"
                    component="p"
                    sx={{
                      fontSize: { xs: '2rem', sm: '2.35rem' },
                      fontWeight: 800,
                      color: 'primary.main',
                    }}
                  >
                    {s.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {s.label}
                  </Typography>
                </Stack>
              </React.Fragment>
            ))}
          </Box>
        </Card>

        <Box id="about" sx={{ mb: 7 }}>
          <SectionHeading
            kicker={ABOUT.kicker}
            title={ABOUT.title}
            subtitle={ABOUT.body}
          />
        </Box>

        <Card sx={{ mb: 7, p: { xs: 2.5, sm: 4 } }}>
          <Typography
            variant="overline"
            color="secondary.main"
            sx={{ fontWeight: 700 }}
          >
            Our school
          </Typography>
          <Typography
            variant="h5"
            component="h2"
            sx={{ mt: 0.5, mb: 1.5 }}
          >
            {SCHOOL_HISTORY.title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.75, whiteSpace: 'pre-line', maxWidth: 760 }}
          >
            {SCHOOL_HISTORY.body}
          </Typography>
        </Card>

        <Box sx={{ mb: 7 }}>
          <SectionHeading
            kicker="Mark your calendar"
            title="Meetings & events"
            subtitle={MEETINGS.cadence}
            id="events"
          />
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {EVENTS.map((ev) => (
              <Card key={ev.title}>
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 3 },
                    alignItems: { sm: 'center' },
                    py: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      minWidth: { sm: 110 },
                      textAlign: { sm: 'center' },
                    }}
                  >
                    <Typography
                      variant="overline"
                      color="secondary.main"
                      sx={{ fontWeight: 800, fontSize: '0.85rem' }}
                    >
                      {ev.date}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 0.5 }}>
                      {ev.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5, lineHeight: 1.65 }}
                    >
                      {ev.detail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ev.location}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography
              variant="overline"
              color="secondary.main"
              sx={{ fontWeight: 700 }}
            >
              2025–2026 schedule
            </Typography>
            <Box
              sx={{
                mt: 1.25,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: { xs: 0.75, sm: 1 },
              }}
            >
              {MEETINGS.schedule.map((m) => (
                <Box
                  key={m.date}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    py: 0.5,
                    alignItems: 'baseline',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      fontVariantNumeric: 'tabular-nums',
                      minWidth: 92,
                    }}
                  >
                    {m.date}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {m.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: 'text.secondary' }}
              >
                Meeting minutes:
              </Typography>
              {MEETING_MINUTES.map((m) => (
                <Link
                  key={m.date}
                  href={m.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  underline="always"
                >
                  {m.date}
                </Link>
              ))}
            </Box>
          </Card>
        </Box>

        <Box sx={{ mb: 7 }}>
          <SectionHeading
            kicker="All year long"
            title="What the PTA sponsors"
            subtitle="From the first fall social to the 5th-grade clap-out, the PTA helps make these traditions happen."
          />
          <Stack spacing={1.5}>
            {SEASONAL_EVENTS.map((s) => {
              const accent = seasonAccents[s.season] ?? theme.palette.primary.main;
              return (
                <Card
                  key={s.season}
                  sx={{
                    overflow: 'hidden',
                    borderLeft: '5px solid',
                    borderLeftColor: accent,
                  }}
                >
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1.5, sm: 3 },
                      alignItems: { sm: 'center' },
                      py: 2.5,
                      '&:last-child': { pb: 2.5 },
                    }}
                  >
                    <Box sx={{ minWidth: { sm: 132 } }}>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ color: accent, fontWeight: 800, lineHeight: 1.1 }}
                      >
                        {s.season}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.items.length} {s.items.length === 1 ? 'event' : 'events'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        flex: 1,
                      }}
                    >
                      {s.items.map((it) => (
                        <Chip
                          key={it}
                          label={it}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            bgcolor: `${accent}14`,
                            border: '1px solid',
                            borderColor: `${accent}33`,
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, fontStyle: 'italic' }}
          >
            {SEASONAL_EVENTS_FOOTNOTE}
          </Typography>
        </Box>

        <Box sx={{ mb: 7 }}>
          <SectionHeading
            kicker="Join us"
            title="How to get involved"
            subtitle="There's a place for every family. Pick what fits your schedule."
            id="involved"
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            {INVOLVEMENT.map((item) => (
              <Card key={item.title} sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Typography
                    variant="overline"
                    color="primary.main"
                    sx={{ fontWeight: 700 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ mt: 0.5, mb: 1 }}
                  >
                    {item.outcome}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7 }}
                  >
                    {item.blurb}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <Button
              href={MEMBERSHIP_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="large"
              sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}
            >
              Join the PTA
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 7 }}>
          <SectionHeading
            kicker="Volunteer"
            title="Become a volunteer"
            subtitle={VOLUNTEER_INTRO}
            id="volunteer"
          />
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {VOLUNTEER_STEPS.map((step, i) => (
              <Card key={step.title}>
                <CardContent
                  sx={{
                    display: 'flex',
                    gap: { xs: 2, sm: 3 },
                    alignItems: 'flex-start',
                    py: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 0.5 }}>
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: step.href || step.links ? 1 : 0, lineHeight: 1.65 }}
                    >
                      {step.detail}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
                    >
                      {step.href ? (
                        <Link
                          href={step.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          underline="always"
                          sx={{ fontWeight: 700 }}
                        >
                          {step.linkLabel}
                        </Link>
                      ) : null}
                      {(step.links ?? []).map((l) => (
                        <Link
                          key={l.label}
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          underline="always"
                          sx={{ fontWeight: 700 }}
                        >
                          {l.label}
                        </Link>
                      ))}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {VOLUNTEER_NOTE}{' '}
            <Link
              href={WCSD_VOLUNTEER_URL}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
              sx={{ fontWeight: 700 }}
            >
              Visit the WCSD volunteer page
            </Link>{' '}
            for full details.
          </Typography>

          <Card sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Typography
              variant="overline"
              color="secondary.main"
              sx={{ fontWeight: 700 }}
            >
              Lead a program
            </Typography>
            <Typography variant="h6" component="h3" sx={{ mt: 0.5, mb: 1.5 }}>
              Coordinator roles
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, maxWidth: 720 }}
            >
              Many roles are looking for a lead — and you don't have to do it
              alone. Roles marked{' '}
              <Box component="span" sx={{ color: 'secondary.main', fontWeight: 700 }}>
                Open!
              </Box>{' '}
              especially need help — click one to email us about volunteering.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 1.25,
              }}
            >
              {COORDINATOR_ROLES.map((r) => {
                const open = r.lead.toLowerCase().includes('open');
                const interactiveProps = open
                  ? {
                      component: 'a',
                      href: volunteerRoleMailto(r.role),
                      'aria-label': `Email us about volunteering for ${r.role}`,
                    }
                  : {};
                return (
                  <Box
                    key={r.role}
                    {...interactiveProps}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: open ? 'secondary.main' : 'divider',
                      bgcolor: open ? 'rgba(22, 163, 74, 0.06)' : 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      height: '100%',
                      ...(open && {
                        textDecoration: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        transition:
                          'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
                        '&:hover': {
                          bgcolor: 'rgba(22, 163, 74, 0.14)',
                          boxShadow: 2,
                        },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'secondary.main',
                          outlineOffset: 2,
                        },
                      }),
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, lineHeight: 1.3 }}
                    >
                      {r.role}
                    </Typography>
                    {open ? (
                      <Chip
                        label="Open — volunteer!"
                        size="small"
                        color="secondary"
                        icon={<MailOutlineIcon />}
                        clickable
                        sx={{ fontWeight: 700, alignSelf: 'flex-start' }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Led by {r.lead}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Card>
        </Box>

        <Box sx={{ mb: 7 }}>
          <SectionHeading
            kicker="Leadership"
            title="Meet the board"
            subtitle="Volunteer parents serving the Hanby community."
            id="board"
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {BOARD.map((member) => (
              <Card key={member.role} sx={{ height: '100%', overflow: 'hidden' }}>
                {member.image ? (
                  <CardMedia
                    component="img"
                    image={member.image}
                    alt={member.alt || `${member.name}, ${member.role}`}
                    sx={{ aspectRatio: '1/1', objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    aria-hidden="true"
                    sx={{
                      aspectRatio: '1/1',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      background: isDark
                        ? 'linear-gradient(180deg, #1e293b 0%, #334155 100%)'
                        : 'linear-gradient(180deg, #eef1f5 0%, #dfe4ea 100%)',
                    }}
                  >
                    <PersonIcon
                      sx={{
                        fontSize: '8.5rem',
                        color: isDark ? '#475569' : '#aeb7c2',
                        mb: '-0.5rem',
                      }}
                    />
                  </Box>
                )}
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ fontWeight: 700 }}
                  >
                    {member.role}
                  </Typography>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ mt: 0.5, mb: 0.75 }}
                  >
                    {member.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {member.blurb}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
          <Card sx={{ mt: 2, p: { xs: 2.5, sm: 3 } }}>
            <Typography
              variant="overline"
              color="secondary.main"
              sx={{ fontWeight: 700 }}
            >
              Also serving this year
            </Typography>
            <Box
              sx={{
                mt: 1.25,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: { xs: 0.75, sm: 1 },
              }}
            >
              {LEADERSHIP_OTHER.map((p) => (
                <Box
                  key={p.role}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    py: 0.5,
                    alignItems: 'baseline',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {p.role}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flexShrink: 0, textAlign: 'right' }}
                  >
                    {p.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Box>

        <Box sx={{ mb: 7 }}>
          <SectionHeading
            kicker="Support our teachers"
            title="Classroom wish lists"
            subtitle={WISH_LISTS_HEADING}
            id="wishlists"
          />
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.25,
            }}
          >
            {WISH_LISTS.map((w) => (
              <Chip
                key={w.name}
                label={w.name}
                component="a"
                href={w.href}
                target="_blank"
                rel="noopener noreferrer"
                clickable
                icon={<OpenInNewIcon sx={{ fontSize: '1rem' }} />}
                sx={{ fontWeight: 600, py: 2, px: 0.5 }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 7 }}>
          <SectionHeading
            kicker="Our story"
            title={HISTORY_ARTICLE.title}
            subtitle={HISTORY_ARTICLE.byline}
            id="history"
          />
          <Card sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Box
              sx={{
                mb: 3,
                textAlign: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 3,
              }}
            >
              <Box
                component="img"
                src={SCHOOL_DRAWING}
                alt="Line drawing of the original Westerville High School building, now Hanby Elementary"
                sx={{
                  width: '100%',
                  maxWidth: 560,
                  height: 'auto',
                  mx: 'auto',
                  display: 'block',
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
              >
                The building at 56 S. State Street — dedicated as Westerville High
                School in 1923, today home to Hanby Elementary.
              </Typography>
            </Box>
            <Stack spacing={3} sx={{ maxWidth: 820 }}>
              {HISTORY_ARTICLE.sections.map((s) => (
                <Box key={s.heading}>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ mb: 1, color: 'primary.main' }}
                  >
                    {s.heading}
                  </Typography>
                  <Stack spacing={1.25}>
                    {s.paragraphs.map((p, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.75 }}
                      >
                        {p}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography
                variant="overline"
                color="secondary.main"
                sx={{ fontWeight: 700 }}
              >
                A look back
              </Typography>
              <Box
                sx={{
                  mt: 1.5,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(3, 1fr)',
                    md: 'repeat(4, 1fr)',
                  },
                  gap: 1.5,
                }}
              >
                {HISTORY_PHOTOS.map((photo, i) => (
                  <Box key={photo.src}>
                    <ButtonBase
                      onClick={() => setLightboxIndex(i)}
                      aria-label={`View larger: ${photo.caption}`}
                      sx={{
                        display: 'block',
                        width: '100%',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& img': { transition: 'transform 0.2s ease' },
                        '&:hover img': { transform: 'scale(1.05)' },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={photo.src}
                        alt=""
                        loading="lazy"
                        sx={{
                          width: '100%',
                          aspectRatio: '4 / 3',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </ButtonBase>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block', lineHeight: 1.35 }}
                    >
                      {photo.caption}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <PhotoLightbox
                photos={HISTORY_PHOTOS}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onNavigate={navigatePhotos}
              />
            </Box>

            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="overline"
                color="secondary.main"
                sx={{ fontWeight: 700 }}
              >
                Archives & further reading
              </Typography>
              <Box
                sx={{
                  mt: 1.25,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 2 },
                }}
              >
                {HISTORY_RESOURCES.map((h) => (
                  <Link
                    key={h.title}
                    href={h.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    underline="always"
                    sx={{ fontWeight: 600 }}
                  >
                    {h.title}
                  </Link>
                ))}
              </Box>
            </Box>
          </Card>
        </Box>

        <Box sx={{ mb: 7 }}>
          <SectionHeading
            kicker="For parents"
            title="Resources & links"
            subtitle="Useful links for Hanby and Westerville families."
            id="resources"
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            {RESOURCES.map((r) => (
              <Card key={r.title} sx={{ height: '100%' }}>
                <CardActionArea
                  component="a"
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ height: '100%', p: 0 }}
                >
                  <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Typography variant="h6" component="h3">
                        {r.title}
                      </Typography>
                      <OpenInNewIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary' }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {r.detail}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>

        <Card
          id="contact"
          sx={{
            p: { xs: 2.5, sm: 4 },
            textAlign: { xs: 'left', sm: 'center' },
            background:
              'linear-gradient(180deg, rgba(30, 58, 138, 0.06) 0%, rgba(245, 158, 11, 0.04) 100%)',
          }}
        >
          <Typography
            variant="overline"
            color="primary.main"
            sx={{ fontWeight: 700 }}
          >
            Get in touch
          </Typography>
          <Typography variant="h5" component="h2" sx={{ mt: 1, mb: 1.5 }}>
            Questions, ideas, or ready to volunteer?
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2.5, maxWidth: 520, mx: 'auto' }}
          >
            We'd love to hear from you. Send us a note and a board member will
            be in touch.
          </Typography>
          <Button
            href={MAILTO}
            variant="contained"
            size="large"
            sx={{ textTransform: 'none', fontWeight: 700, px: 4 }}
          >
            Email {CONTACT_EMAIL}
          </Button>
        </Card>
      </Container>
      </Box>

      <Box
        component="footer"
        className="App-footer"
        sx={{ py: 3, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} {PTA_NAME} ·{' '}
            <Link href={MAILTO} color="inherit" underline="always">
              Contact
            </Link>{' '}
            ·{' '}
            <Link
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="always"
            >
              Facebook
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Member PTA of{' '}
            <Link
              href="https://www.ohiopta.org"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="always"
            >
              PTA Ohio Congress
            </Link>
            {' '}(PTA ID 00032089, Donation EIN: 31-1407545)
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default App;
